import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowElementProps, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_HANGAR_SELL_FLOW_PATH } from '../../../../../services/routes/routes'
import { propertyOwnerHangarSellSchema } from './propertyOwnerHangarSellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_REALTOR_LEAD_ENGINE,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  buildingType,
  mainFloorSpaceField,
  plotAreaField,
  priceField,
  purchasePriceOnRequestToggle,
  separableSpaceField,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { SeparableSpaceType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/separableSpaceField/SeparableSpaceField'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import {
  crane,
  estateFeatures,
  flooringType,
  floorLoadField,
  freeFrom,
  interiorQuality,
  loadLifting,
  parkingSpace,
  powerSupplyField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { RealtorLeadEngine } from '../../../../../components/pages/insertionPage/screens/realtorLeadEngine/RealtorLeadEngine'

const propertyOwnerHangarSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.hangarRentSell'
        properties={[
          buildingType([
            BuildingTypeOption.SHOWROOM_SPACE,
            BuildingTypeOption.HALL,
            BuildingTypeOption.HIGH_LACK_STORAGE,
            BuildingTypeOption.INDUSTRY_HALL,
            BuildingTypeOption.INDUSTRY_HALL_WITH_OPEN_AREA,
            BuildingTypeOption.COLD_STORAGE,
            BuildingTypeOption.MULTIDECK_CABINET_STORAGE,
            BuildingTypeOption.STORAGE_WITH_OPEN_AREA,
            BuildingTypeOption.STORAGE_AREA,
            BuildingTypeOption.STORAGE_HALL,
            BuildingTypeOption.SERVICE_AREA,
            BuildingTypeOption.SHIPPING_STORAGE,
            BuildingTypeOption.REPAIR_SHOP,
          ]),
          totalFloorSpaceField('left'),
          plotAreaField('right', undefined),
          mainFloorSpaceField(MainFloorType.STORAGE_AND_PRODUCTION_AREA, 'left', undefined),
          separableSpaceField(SeparableSpaceType.STORAGE_AND_PRODUCTION_AREA, 'right', undefined),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('left')),
          freeFrom(getDefaultClasses('right')),
          purchasePriceOnRequestToggle(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props: FlowElementProps) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allSell'
        properties={[
          estateFeatures([
            RealEstateFeatures.PERSON_ELEVATOR,
            RealEstateFeatures.RAMP,
            RealEstateFeatures.LIFTING_PLATFORM,
          ]),
          interiorQuality(),
          flooringType(),
          floorLoadField(getDefaultClasses('left')),
          powerSupplyField(getDefaultClasses('right')),
          loadLifting(),
          crane(),
          parkingSpace({ showParkingSpaceType: false }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_REALTOR_LEAD_ENGINE],
    element: (props) => <RealtorLeadEngine {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerHangarSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.hangar',
    offerType: 'wizard.navigation.header.sells',
    path: PROPERTY_OWNER_HANGAR_SELL_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_HANGAR_SELL,
    validationSchema: propertyOwnerHangarSellSchema,
    priceToApiMapper: priceToApiSell,
    metaData: {
      category: RealEstateCategory.PRODUCTION_HALL,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: { hideStreetAndStreetNumber: false },
  flowScreens: propertyOwnerHangarSellFlowScreens,
}
